/**
 * Stock on a treatment's lines: its drugs (each may be taken from the
 * Warehouse, as a Stock Issue of its Medication, ADR 0026) and its non-drug
 * materials. Pure mappers between the API lines, the controlled rows the
 * editors hold, and the medication form's "Z magazynu" values. Hook-free:
 * the medication form reaches it (notes-2 F-2).
 */
import { v4 as uuidv4 } from 'uuid';
import {
  AssociatedMedicineDtoDosageIntervalEnum,
  GetTreatmentMaterialDto,
  TreatmentMaterialInputDto,
} from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import {
  StockHandover,
  StockMeasure,
  formatMilli,
  parseStockQuantity,
  toStockInput,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import {
  EMPTY_STOCK_VALUES,
  MedicationStockFormValues,
  prescriptionChoice,
  savedAsRx,
} from './medication-form/medicationStockValues';

export interface AssociatedMedicineValue {
  /** Saved line id; sent back so the line keeps its Stock Issue (R2-3). */
  id?: string;
  /** Null or absent for a medicine typed by hand, off the RPL registry. */
  medicineProductId?: string | null;
  medicineName: string;
  dosage?: number | null;
  dosageInterval?: AssociatedMedicineDtoDosageIntervalEnum | null;
  notes?: string | null;
  preparationType?: string | null;
  /** Warehouse Product the drug is taken from (Facility only). */
  productId?: string;
  quantity?: number;
  measure?: StockMeasure;
  handover?: StockHandover;
  /** Handed-over Rx only (review D4). */
  prescriptionIssued?: boolean;
  /** Handed-over only: who received the drug; absent lets the backend name the owner. */
  recipientName?: string;
  /** Handed-over only: their address; absent lets the backend take the owner's (#688 R1-2). */
  recipientAddress?: string;
}

/**
 * A drug line as the API returns it: a catalogue template's, a Performed
 * Treatment's or an overview row's. Every generated line DTO is assignable
 * to it (R2-3).
 */
export interface AssociatedMedicineSource {
  id?: string;
  medicineProductId?: string | null;
  medicineName: string;
  dosage?: number | null;
  dosageInterval?: AssociatedMedicineDtoDosageIntervalEnum | null;
  notes?: string | null;
  preparationType?: string | null;
  productId?: string;
  quantityMilli?: number;
  handover?: StockHandover;
  prescriptionIssued?: boolean;
  recipientName?: string;
  recipientAddress?: string;
}

/** A non-drug material line of a treatment (ADR 0026: drugs go through Medications). */
export interface TreatmentMaterialValue {
  /** Saved line id; sent back so the line keeps its movements. */
  id?: string;
  /**
   * An unsaved line's React key, given once when the line appears; never
   * sent (`materialsToPayload`). A saved line is keyed by its `id`.
   */
  clientKey?: string;
  productId: string;
  productName: string;
  quantity: number;
  measure: StockMeasure;
}

type RowStock = Pick<
  AssociatedMedicineValue,
  | 'productId'
  | 'quantity'
  | 'measure'
  | 'handover'
  | 'prescriptionIssued'
  | 'recipientName'
  | 'recipientAddress'
>;

const NO_ROW_STOCK: RowStock = {
  productId: undefined,
  quantity: undefined,
  measure: undefined,
  handover: undefined,
  prescriptionIssued: undefined,
  recipientName: undefined,
  recipientAddress: undefined,
};

const MILLI_PER_UNIT = 1000;

/** "2,5": Phase 1's quantity text, read from the whole thousandths. */
export function formatStockQuantity(quantity: number): string {
  return formatMilli(Math.round(quantity * MILLI_PER_UNIT));
}

export function measureShortLabel(measure: StockMeasure | undefined): string {
  return translateKey(
    measure === 'PACKAGE'
      ? 'warehouse.clinical.measureShortPackage'
      : 'warehouse.clinical.measureShortUnit',
  );
}

/**
 * `keepLineIds` is false when a catalogue template prefills a Performed
 * Treatment: its lines are new, so they must not reuse the template's ids.
 */
export function toAssociatedMedicineValues(
  lines: AssociatedMedicineSource[],
  keepLineIds = true,
): AssociatedMedicineValue[] {
  return lines.map((line) => ({
    ...(keepLineIds && line.id ? { id: line.id } : {}),
    medicineProductId: line.medicineProductId,
    medicineName: line.medicineName,
    dosage: line.dosage,
    dosageInterval: line.dosageInterval,
    notes: line.notes,
    preparationType: line.preparationType,
    ...(line.productId && line.quantityMilli
      ? {
          productId: line.productId,
          ...toStockInput(line.quantityMilli),
          handover: line.handover ?? 'ADMINISTERED',
          ...(typeof line.prescriptionIssued === 'boolean'
            ? { prescriptionIssued: line.prescriptionIssued }
            : {}),
          ...(line.recipientName ? { recipientName: line.recipientName } : {}),
          ...(line.recipientAddress
            ? { recipientAddress: line.recipientAddress }
            : {}),
        }
      : {}),
  }));
}

/**
 * The row's stock from the medication form's "Z magazynu" values. The
 * batch is not carried: a treatment drug line always allocates FEFO on the
 * backend, so its form hides "Partia" (F-7).
 */
export function rowStockFromForm(values: MedicationStockFormValues): RowStock {
  // The switch first: the resolver casts an untouched quantity to undefined,
  // which a line not taken from stock submits.
  if (!values.stockEnabled || !values.stockProductId) {
    return { ...NO_ROW_STOCK };
  }
  // The field may still hold the typed text (unconfirmed resend, F-3).
  const quantity = parseStockQuantity(values.stockQuantity);
  // As `buildStockPayload`: the schema holds a taking without its handover.
  const handover = values.stockHandover;
  if (quantity === null || !handover) return { ...NO_ROW_STOCK };
  const handedOver = handover === 'HANDED_OVER';
  const recipientName = values.stockRecipientName.trim();
  const recipientAddress = values.stockRecipientAddress.trim();
  return {
    productId: values.stockProductId,
    quantity,
    measure: values.stockMeasure,
    handover,
    // P7B-R1: as `buildStockPayload`, only an Rx drug records a prescription.
    prescriptionIssued:
      handedOver && values.stockProductIsRx && values.stockPrescription
        ? values.stockPrescription === 'ISSUED'
        : undefined,
    recipientName: handedOver && recipientName ? recipientName : undefined,
    recipientAddress:
      handedOver && recipientAddress ? recipientAddress : undefined,
  };
}

/**
 * The form's "Z magazynu" values for a row being edited. Only a Performed
 * Treatment's saved line (one with an id) has already taken its stock, so
 * only then does the shortage check count that quantity as available again
 * and an opt-out give it back (`stockWasSet`). A catalogue template's line
 * never takes stock: its Product and quantity are defaults for the
 * treatments it prefills.
 */
export function formStockFromRow(
  row: AssociatedMedicineValue,
  isTemplate = false,
): MedicationStockFormValues {
  if (!row.productId || row.quantity === undefined) return EMPTY_STOCK_VALUES;
  return {
    stockEnabled: true,
    stockProductId: row.productId,
    stockQuantity: row.quantity,
    stockMeasure: row.measure ?? 'UNIT',
    stockHandover: row.handover ?? 'ADMINISTERED',
    stockBatchId: '',
    stockPrescription: prescriptionChoice(row.prescriptionIssued),
    stockRecipientName: row.recipientName ?? '',
    stockRecipientAddress: row.recipientAddress ?? '',
    stockProductIsRx: savedAsRx(row.handover, row.prescriptionIssued),
    stockWasSet: !isTemplate && Boolean(row.id),
  };
}

/** An owner never sends stock; a row prefilled elsewhere loses it here. */
export function withoutRowStock(
  row: AssociatedMedicineValue,
): AssociatedMedicineValue {
  return { ...row, ...NO_ROW_STOCK };
}

/**
 * "Z magazynu: 2 jedn., podany w placówce", or null for a row without stock.
 * The handover is mid-sentence, so it has its own lower-case copy.
 */
export function describeRowStock(row: AssociatedMedicineValue): string | null {
  if (!row.productId || row.quantity === undefined) return null;
  return translateKey('warehouse.clinical.rowStock', {
    quantity: formatStockQuantity(row.quantity),
    measure: measureShortLabel(row.measure),
    handover: translateKey(
      row.handover === 'HANDED_OVER'
        ? 'warehouse.clinical.rowStockHandedOver'
        : 'warehouse.clinical.rowStockAdministered',
    ),
  });
}

/** `keepLineIds`: false for a catalogue default prefilling a new record. */
export function materialsFromServer(
  materials?: GetTreatmentMaterialDto[],
  keepLineIds = true,
): TreatmentMaterialValue[] {
  return (materials ?? []).map((material) => ({
    ...(keepLineIds && material.id
      ? { id: material.id }
      : { clientKey: uuidv4() }),
    productId: material.productId,
    productName: material.productName ?? '',
    ...toStockInput(material.quantityMilli),
  }));
}

export function materialsToPayload(
  values: TreatmentMaterialValue[],
): TreatmentMaterialInputDto[] {
  return values.map((value) => ({
    ...(value.id ? { id: value.id } : {}),
    productId: value.productId,
    quantity: value.quantity,
    measure: value.measure,
  }));
}
